/**
 * FIND-02 — frecency & adaptive history (spec §4.2). Pure, deterministic scoring the Cmd+K switcher
 * layers on top of the fuzzy match. No mainstream matcher ships this; the model is Firefox Places'
 * (exponential-decay frecency + an adaptive query→pick memory). Kept framework-free so it unit-tests
 * in plain Node; the IndexedDB persistence is a thin wrapper (app layer).
 */

const DAY_MS = 86_400_000;

/** Frecency half-life: a visit's weight halves every 30 days (spec §4.2). */
export const FRECENCY_HALF_LIFE_MS = 30 * DAY_MS;

/** Bonus multiplier applied to a starred item's frecency (starred bucket, spec §4.2). */
export const STARRED_FRECENCY_BONUS = 1.5;

/** How many recent opens to retain per note — enough for a stable score, bounded storage. */
export const MAX_OPENS_TRACKED = 10;

/**
 * Frecency of an item from its recent open timestamps: Σ 2^(−age / halfLife). Recent, frequent opens
 * score high; old ones fade smoothly. `starred` applies a constant bucket bonus. Future timestamps
 * (clock skew) are clamped to `now` so they can't inflate the score.
 */
export function frecency(
  opens: readonly number[],
  now: number,
  opts: { starred?: boolean; halfLifeMs?: number } = {},
): number {
  const halfLife = opts.halfLifeMs ?? FRECENCY_HALF_LIFE_MS;
  let score = 0;
  for (const t of opens) {
    const age = Math.max(0, now - t);
    score += Math.pow(2, -age / halfLife);
  }
  return opts.starred ? score * STARRED_FRECENCY_BONUS : score;
}

/** Record a new open, keeping only the most recent MAX_OPENS_TRACKED timestamps (newest first). */
export function recordOpen(opens: readonly number[], at: number, cap = MAX_OPENS_TRACKED): number[] {
  return [at, ...opens].sort((a, b) => b - a).slice(0, cap);
}

// ─── Adaptive history: query → picked-id memory ──────────────────────────────────────────────────

/** Per-day multiplicative decay of an adaptive-history entry's use-count (spec §4.2: ×0.975/day). */
export const ADAPTIVE_DECAY_PER_DAY = 0.975;

/** An adaptive-history entry expires after this long unused (spec §4.2: 90 days). */
export const ADAPTIVE_EXPIRY_MS = 90 * DAY_MS;

export interface AdaptiveEntry {
  /** The (normalized, lowercased) query the user had typed when they picked. */
  query: string;
  /** The id they picked (noteId or entityId). */
  pickedId: string;
  /** Number of times this (query → pick) pair has been reinforced. */
  count: number;
  /** Epoch ms of the most recent reinforcement. */
  lastUsed: number;
}

/** Normalize a raw query for adaptive lookup/keys: trimmed, collapsed whitespace, lowercased. */
export function normalizeQuery(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase();
}

/** Reinforce (or create) the (query → pickedId) pair, returning the updated entry list. */
export function reinforce(entries: readonly AdaptiveEntry[], rawQuery: string, pickedId: string, now: number): AdaptiveEntry[] {
  const query = normalizeQuery(rawQuery);
  if (!query) return [...entries];
  const out = entries.map((e) => ({ ...e }));
  const hit = out.find((e) => e.query === query && e.pickedId === pickedId);
  if (hit) {
    hit.count += 1;
    hit.lastUsed = now;
  } else {
    out.push({ query, pickedId, count: 1, lastUsed: now });
  }
  return out;
}

/**
 * Adaptive boost per candidate id for the query the user is typing. An entry contributes when its
 * stored query STARTS WITH the current typed text (so typing "b","bu","bud" keeps surfacing a note
 * previously picked for "budget review"). Score = count × decay^days-since-used; expired/stale
 * entries contribute nothing. Returns a map id → boost (0 when none).
 */
export function adaptiveBoosts(entries: readonly AdaptiveEntry[], rawTyped: string, now: number): Map<string, number> {
  const typed = normalizeQuery(rawTyped);
  const out = new Map<string, number>();
  if (!typed) return out;
  for (const e of entries) {
    if (now - e.lastUsed > ADAPTIVE_EXPIRY_MS) continue;
    if (!e.query.startsWith(typed)) continue;
    const days = Math.max(0, (now - e.lastUsed) / DAY_MS);
    const score = e.count * Math.pow(ADAPTIVE_DECAY_PER_DAY, days);
    out.set(e.pickedId, Math.max(out.get(e.pickedId) ?? 0, score));
  }
  return out;
}

/** Drop entries unused past the expiry window (call opportunistically to keep the store small). */
export function pruneAdaptive(entries: readonly AdaptiveEntry[], now: number): AdaptiveEntry[] {
  return entries.filter((e) => now - e.lastUsed <= ADAPTIVE_EXPIRY_MS);
}
